import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Copy, Check, Phone, Mail, ExternalLink, Info } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import toast from "react-hot-toast";
import logger from "../../../utils/logger";
import ContactLinksGenerator from "../../../utils/contactlinksgenerator";
// Note: MdOutlineEdit, IoDocumentOutline removed - no longer needed


interface InformationProps {
  leadToBeUpdate: string;
  leadData: any; // ← ADD this prop
 
}

const Information: FC<InformationProps> = ({ leadToBeUpdate, leadData: passedLeadData }) => {
  const stateData = useSelector((state: any) => state?.rootReducers);
  const [leadData, setLeadData] = useState<Record<string, any>>({});
  const [customFields, setCustomFields] = useState<Record<string, any>>({});

 

useEffect(() => {
  logger.log('📊 Information useEffect - hasData:', !!passedLeadData);
  if (passedLeadData && Object.keys(passedLeadData).length > 0) {
    processLeadData(passedLeadData);
  }
}, [passedLeadData]);

const processLeadData = (leadData: any) => {
  try {
    logger.log('🔄 Processing lead data in Information');
    const extraDetails = leadData.extraDetails || {};

    // Get customForm to identify group fields
    const customForm = stateData?.userData?.userDetails?.userPreference?.customForm || [];

    // Process extraDetails for both grouped and regular fields
    const { groupedFields, regularFields } = processExtraDetails(extraDetails, customForm);
    setCustomFields(regularFields);
    setLeadData({ groupedFields });
  } catch (error) {
    console.error("Error processing lead data:", error);
    setCustomFields({});
    setLeadData({});
  }
};

const processExtraDetails = (details: Record<string, any>, customForm: any[]): { groupedFields: any[], regularFields: Record<string, any> } => {
  const groupedFields: any[] = [];
  const regularFields: Record<string, any> = {};

  // Build a map of field values to their types from customForm
  logger.log('📋 CustomForm received:', customForm);
  const groupFieldValues = new Set<string>();
  customForm.forEach((field: any) => {
    if (field.type === 'group') {
      groupFieldValues.add(field.value);
      logger.log(`🔍 Registered group field: ${field.value} (${field.label})`);
    }
  });
  logger.log('📦 All registered group fields:', Array.from(groupFieldValues));

  try {
    Object.entries(details).forEach(([key, value]) => {
      // Skip null/undefined keys
      if (!key) return;

      try {
        const formattedKey = key
          .replace(/_/g, " ")
          .replace(/\?$/, "")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // ============================================
        // 🆕 ENHANCED: Handle ALL group fields by checking customForm
        // ============================================
        const isGroupField = groupFieldValues.has(key);
        logger.log(`🔍 Checking key: ${key}, isGroupField: ${isGroupField}, valueType: ${Array.isArray(value) ? 'array' : typeof value}`);

        if (isGroupField && Array.isArray(value) && value.length > 0) {
          logger.log(`📦 Processing group field array: ${key}`, value);

          // Each item in the array is a separate group instance
          value.forEach((groupInstance, instanceIndex) => {
            if (typeof groupInstance === 'object' && groupInstance !== null) {
              groupedFields.push({
                _isGroup: true,
                _groupTitle: formattedKey + (value.length > 1 ? ` #${instanceIndex + 1}` : ''),
                _groupFields: groupInstance
              });
            }
          });
          return; // Skip adding to regular fields
        }

        // Handle single group object (non-array)
        if (isGroupField && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          logger.log(`📦 Processing single group field object: ${key}`, value);
          groupedFields.push({
            _isGroup: true,
            _groupTitle: formattedKey,
            _groupFields: value
          });
          return; // Skip adding to regular fields
        }

        // Handle question_N grouped fields (like question_1, question_2, etc.)
        if (key.startsWith('question_') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // This contains nested group data
          Object.entries(value).forEach(([groupName, groupData]) => {
            if (typeof groupData === 'object' && groupData !== null && !Array.isArray(groupData)) {
              groupedFields.push({
                _isGroup: true,
                _groupTitle: groupName.replace(/_/g, ' '),
                _groupFields: groupData
              });
            }
          });
        }
        // Handle field_data array specially
        else if (key === "field_data" && Array.isArray(value)) {
          value.forEach((field: any) => {
            if (field?.name && field?.values !== undefined) {
              const fieldLabel = field.name
                .replace(/_/g, " ")
                .replace(/\?$/, "")
                .split(" ")
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              const fieldValue = Array.isArray(field.values)
                ? field.values.join(", ")
                : field.values;

              regularFields[fieldLabel] = fieldValue;
            }
          });
        }
        // Handle whatsappMessage specially
        else if (key === "whatsappMessage" && typeof value === "object" && value?.body) {
          if (value.caption) {
            regularFields["WhatsappMessage"] = value.caption;
          } else {
            regularFields["WhatsappMessage"] = value.body;
          }
        }
        // Handle other objects - expand sub-fields with parent prefix for full visibility
        else if (typeof value === "object" && value !== null) {
          const SKIP_FIELDS = new Set(['sha256', 'mime_type', 'id', 'ctwa_clid', 'source_id']);
          const subEntries = Object.entries(value).filter(
            ([k, v]) => !SKIP_FIELDS.has(k) && v !== null && v !== undefined && v !== ''
          );

          if (subEntries.length === 0) return;

          if (subEntries.length === 1) {
            // Single meaningful field → show directly under parent key
            regularFields[formattedKey] = subEntries[0][1];
          } else {
            // Multiple fields → expand each with parent key prefix for context
            subEntries.forEach(([subKey, subVal]) => {
              const subFormatted = subKey
                .replace(/_/g, ' ')
                .split(' ')
                .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
              regularFields[`${formattedKey}: ${subFormatted}`] = subVal;
            });
          }
        }
        // Handle primitive values
        else if (value !== null && value !== undefined && value !== "") {
          if (Array.isArray(value) && value.length === 0) {
            regularFields[formattedKey] = "No data available";
          } else {
            regularFields[formattedKey] = value;
          }
        }
      } catch (fieldError) {
        console.warn(`Error processing field ${key}:`, fieldError);
      }
    });
  } catch (error) {
    console.error("Error processing extra details:", error);
  }

  return { groupedFields, regularFields };
};

  const ExpandableText: FC<{ text: string; maxLength: number }> = ({ text, maxLength }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    return (
      <div style={{ wordBreak: "break-word" }}>
        {isExpanded ? text : `${text.slice(0, maxLength)}...`}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: "none",
            border: "none",
            color: "#007bff",
            cursor: "pointer",
            padding: "0",
            marginLeft: "6px",
            fontSize: "12px",
            fontFamily: '"Gilroy-Medium", sans-serif',
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </button>
      </div>
    );
  };

  // Smart field type detection
  const detectFieldType = (key: string, value: any): 'phone' | 'email' | 'url' | 'image' | 'date' | 'text' => {
    const keyLower = key.toLowerCase();
    const valStr = String(value || '').trim();

    // Image URL detection (before generic url check)
    if (
      (keyLower.includes('image') || keyLower.includes('photo') || keyLower.includes('thumbnail')) &&
      (valStr.startsWith('http://') || valStr.startsWith('https://'))
    ) return 'image';

    if (keyLower.includes('phone') || keyLower.includes('mobile') || keyLower.includes('whatsapp') || keyLower.includes('contact_number')) {
      if (/^[\+]?[\d\s\-()]{7,15}$/.test(valStr.replace(/\s/g, ''))) return 'phone';
    }
    if (keyLower.includes('email') || keyLower.includes('mail')) {
      if (valStr.includes('@')) return 'email';
    }
    if (keyLower.includes('website') || keyLower.includes('url') || keyLower.includes('link')) return 'url';
    if (valStr.startsWith('http://') || valStr.startsWith('https://') || valStr.startsWith('www.')) return 'url';
    // Check value patterns
    if (/^[\w.-]+@[\w.-]+\.\w+$/.test(valStr)) return 'email';
    // Date detection: ISO 8601 format or common date patterns
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(valStr)) return 'date';
    if (keyLower.includes('date') || keyLower.includes('created') || keyLower.includes('updated') || keyLower.includes('time')) {
      if (!isNaN(Date.parse(valStr)) && valStr.length > 8) return 'date';
    }
    return 'text';
  };

  // Format a value string: replace underscores with spaces, capitalize
  const formatDisplayValue = (val: string): string => {
    if (!val) return val;
    // Replace underscores with spaces
    return val.replace(/_/g, ' ');
  };

  // Format date to readable string
  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return dateStr; }
  };

  // Copy helper
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyValue = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success("Copied!");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch { toast.error("Failed to copy"); }
  };

  // Render a smart field value with actions
  const renderSmartValue = (key: string, value: any) => {
    const valStr = String(value || '').trim();
    if (!valStr || valStr === '-') return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' }}>-</span>;

    const fieldType = detectFieldType(key, value);
    const MAX_LENGTH = 150;

    // Long text handling
    if (typeof value === 'string' && value.length > MAX_LENGTH && fieldType === 'text') {
      return <ExpandableText text={formatDisplayValue(value)} maxLength={MAX_LENGTH} />;
    }

    // Boolean
    if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;

    // Number
    if (typeof value === 'number') return <span>{value.toLocaleString()}</span>;

    // Array
    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: '#9ca3af' }}>-</span>;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {value.map((item, i) => (
            <span key={i} style={{
              padding: '2px 8px', background: '#dbeafe', color: '#1e40af',
              borderRadius: '10px', fontSize: '11px', fontFamily: '"Gilroy-Medium", sans-serif',
            }}>{formatDisplayValue(String(item))}</span>
          ))}
        </div>
      );
    }

    const actionBtnStyle: React.CSSProperties = {
      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
      borderRadius: '4px', display: 'inline-flex', alignItems: 'center',
      color: '#6b7280', transition: 'all 0.15s',
    };

    switch (fieldType) {
      case 'phone': {
        const contactObj = new ContactLinksGenerator({ number: valStr, email: '', message: 'Hello' });
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{valStr}</span>
            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              {contactObj.tel && (
                <button style={actionBtnStyle} onClick={() => window.open(contactObj.tel, '_blank')} title="Call">
                  <Phone size={13} color="#007bff" />
                </button>
              )}
              {contactObj.whatsapp && (
                <button style={actionBtnStyle} onClick={() => window.open(contactObj.whatsapp, '_blank')} title="WhatsApp">
                  <FaWhatsapp size={13} color="#25D366" />
                </button>
              )}
              <button style={actionBtnStyle} onClick={() => copyValue(valStr, key)} title="Copy">
                {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        );
      }
      case 'email':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <a href={`mailto:${valStr}`} style={{ color: '#007bff', textDecoration: 'none', fontSize: '13px' }}>{valStr}</a>
            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              <button style={actionBtnStyle} onClick={() => window.open(`mailto:${valStr}`, '_blank')} title="Send email">
                <Mail size={13} color="#007bff" />
              </button>
              <button style={actionBtnStyle} onClick={() => copyValue(valStr, key)} title="Copy">
                {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        );
      case 'image': {
        const href = valStr.startsWith('http') ? valStr : `https://${valStr}`;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <img
              src={href}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '140px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #f0f0f0', background: '#fafafa' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <a href={href} target="_blank" rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'none', fontSize: '12px', flex: 1 }}>
                View image
              </a>
              <button style={actionBtnStyle} onClick={() => copyValue(valStr, key)} title="Copy URL">
                {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        );
      }
      case 'url': {
        const href = valStr.startsWith('http') ? valStr : `https://${valStr}`;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <a href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'none', fontSize: '13px', wordBreak: 'break-all', flex: 1 }}>
              {valStr.length > 60 ? valStr.slice(0, 60) + '...' : valStr}
            </a>
            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
              <button style={actionBtnStyle} onClick={() => window.open(href, '_blank', 'noopener,noreferrer')} title="Open link">
                <ExternalLink size={13} color="#007bff" />
              </button>
              <button style={actionBtnStyle} onClick={() => copyValue(valStr, key)} title="Copy">
                {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        );
      }
      case 'date':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ flex: 1 }}>{formatDate(valStr)}</span>
            <button style={{ ...actionBtnStyle, flexShrink: 0 }} onClick={() => copyValue(formatDate(valStr), key)} title="Copy">
              {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
            </button>
          </div>
        );
      default: {
        const displayVal = formatDisplayValue(valStr);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ flex: 1, wordBreak: 'break-word' }}>{displayVal}</span>
            {displayVal.length > 3 && (
              <button style={{ ...actionBtnStyle, flexShrink: 0 }} onClick={() => copyValue(displayVal, key)} title="Copy">
                {copiedKey === key ? <Check size={13} color="#28a745" /> : <Copy size={13} />}
              </button>
            )}
          </div>
        );
      }
    }
  };

 // Collect ALL fields into a flat list
  const allFields: Array<{ key: string; value: any; group?: string }> = [];

  // Website & Address from lead data
  if (passedLeadData?.website) allFields.push({ key: 'Website', value: passedLeadData.website });
  if (passedLeadData?.address) allFields.push({ key: 'Address', value: passedLeadData.address });

  // WhatsApp message
  if (passedLeadData?.extraDetails?.whatsappMessage?.body) {
    allFields.push({ key: 'WhatsApp Message', value: passedLeadData.extraDetails.whatsappMessage.body });
  }

  // Grouped fields from custom forms
  if (leadData?.groupedFields && leadData.groupedFields.length > 0) {
    leadData.groupedFields.forEach((group: any) => {
      Object.entries(group._groupFields || {}).forEach(([fieldKey, fieldValue]: [string, any]) => {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') return;
        const fieldLabel = fieldKey.replace(/_/g, ' ').replace(/:/g, '').split(' ')
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        // Handle location objects
        let val = fieldValue;
        if (typeof fieldValue === 'object' && fieldValue !== null && !Array.isArray(fieldValue)) {
          val = fieldValue.address || JSON.stringify(fieldValue);
        }
        allFields.push({ key: fieldLabel, value: val, group: group._groupTitle });
      });
    });
  }

  // Regular custom fields
  Object.entries(customFields).forEach(([key, value]) => {
    allFields.push({ key, value });
  });

  const hasContent = allFields.length > 0;

  // Shared styles
  const fieldStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderBottom: '1px solid #f0f0f0',
    fontFamily: '"Gilroy-Medium", "Gilroy", sans-serif',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: '"Gilroy-Bold", "Gilroy", sans-serif',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.2,
    marginBottom: '2px',
  };
  const valueStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"Gilroy-Medium", "Gilroy", sans-serif',
    color: '#1a1a1a',
    lineHeight: 1.4,
  };

  return (
    <div style={{ fontFamily: '"Gilroy-Medium", "Gilroy", sans-serif' }}>
      {hasContent ? (
        <div>
          {allFields.map((field, index) => (
            <div key={`${field.key}-${index}`} style={{
              ...fieldStyle,
              ...(index === allFields.length - 1 ? { borderBottom: 'none' } : {}),
            }}>
              <div style={labelStyle}>{field.key}</div>
              <div style={valueStyle}>
                {renderSmartValue(field.key, field.value)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '48px 20px', textAlign: 'center', minHeight: '200px',
        }}>
          <Info size={48} strokeWidth={1.2} style={{ color: '#d1d5db', marginBottom: '12px' }} />
          <p style={{ color: '#9ca3af', fontSize: '14px', fontFamily: '"Gilroy-Medium", sans-serif', margin: 0 }}>
            {!passedLeadData ? "Loading..." : "No additional information"}
          </p>
        </div>
      )}
    </div>
  );
};

export default Information;